import React, { useState, useCallback } from 'react';
import { Box, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import AppButton from '../buttons/AppButton';

/**
 * Usage — imperative (recommended, mirrors window.confirm pattern):
 *
 *   const confirm = useAppConfirm();
 *   const ok = await confirm({ message: 'Eliminare questo record?' });
 *   if (!ok) return;
 *
 * Usage — declarative (controlled):
 *
 *   <AppConfirmDialog
 *     open={open}
 *     message="Eliminare questo record?"
 *     onConfirm={() => doDelete()}
 *     onCancel={() => setOpen(false)}
 *   />
 *
 * Props:
 *   message      — main body text (required)
 *   title        — dialog title (default "Conferma")
 *   confirmLabel — confirm button label (default "Conferma")
 *   cancelLabel  — cancel button label (default "Annulla")
 *   variant      — AppButton variant for the confirm button (default "danger")
 *   icon         — FontAwesome class for the header icon (default "fa-exclamation-triangle")
 */
export default function AppConfirmDialog({
  open,
  message,
  title = 'Conferma',
  confirmLabel = 'Conferma',
  cancelLabel = 'Annulla',
  variant = 'danger',
  icon = 'fa-solid fa-exclamation-triangle',
  onConfirm,
  onCancel,
}) {
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="xs"
      fullWidth
      // Above the legacy GenericModal (--z-modal), which a confirm is often asked from.
      sx={{ zIndex: 'var(--z-modal-dialog)' }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 'var(--radius-md)',
            background: `var(--color-${variant}-container)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: `var(--color-${variant})`,
            fontSize: 15,
            flexShrink: 0,
          }}
        >
          <i className={icon} />
        </Box>
        <Box sx={{ fontSize: 16, fontWeight: 700, color: 'var(--color-primary)' }}>{title}</Box>
      </DialogTitle>

      <DialogContent sx={{ pt: '8px !important', pb: 1 }}>
        <Box sx={{ fontSize: 14, color: 'var(--color-on-surface-dim)', lineHeight: 1.6 }}>
          {message}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <AppButton mode="ghost" variant="neutral" size="sm" onClick={onCancel}>
          {cancelLabel}
        </AppButton>
        <AppButton mode="nav" variant={variant} size="sm" onClick={onConfirm}>
          {confirmLabel}
        </AppButton>
      </DialogActions>
    </Dialog>
  );
}

// ── Imperative hook ─────────────────────────────────────────────────────────

const AppConfirmContext = React.createContext(null);

export function AppConfirmProvider({ children }) {
  const [state, setState] = useState({ open: false, props: {}, resolve: null });

  const confirm = useCallback((props) => {
    return new Promise((resolve) => {
      setState({ open: true, props, resolve });
    });
  }, []);

  const handleConfirm = () => {
    state.resolve(true);
    setState((s) => ({ ...s, open: false }));
  };

  const handleCancel = () => {
    state.resolve(false);
    setState((s) => ({ ...s, open: false }));
  };

  return (
    <AppConfirmContext.Provider value={confirm}>
      {children}
      <AppConfirmDialog
        open={state.open}
        {...state.props}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </AppConfirmContext.Provider>
  );
}

export function useAppConfirm() {
  const confirm = React.useContext(AppConfirmContext);
  if (!confirm) throw new Error('useAppConfirm must be used inside <AppConfirmProvider>');
  return confirm;
}
